import type { AdminPermission } from '@carcheck/shared';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router-dom';

import { AuthGate } from './AuthGate';
import { Routes, type AppRoutePath } from './routes';
import { RequirePermission } from '../auth';
import { AppShell } from '../components/shell';
import { AppealsListPage } from '../pages/appeals';
import { LoginPage, ResetConfirmPage, ResetRequestPage, SetPasswordPage } from '../pages/auth';
import { CardsListPage } from '../pages/cards';
import { CompaniesListPage, CompanyCreatePage, CompanyDetailPage } from '../pages/companies';
import { ConfigPage } from '../pages/config';
import { DashboardPage } from '../pages/dashboard';
import { HealthPage } from '../pages/health';
import { InspectorsListPage } from '../pages/inspectors';
import { MoneyHealthPage } from '../pages/money-health';
import { NotFoundPage } from '../pages/NotFoundPage';
import { OrdersListPage } from '../pages/orders';
import { PayoutsListPage } from '../pages/payouts';
import { PickersListPage } from '../pages/pickers';
import { RefundsListPage } from '../pages/refunds';
import { ReportsListPage } from '../pages/reports';
import { UsersListPage } from '../pages/users';
import { VehicleCatalogPage } from '../pages/vehicle-catalog';

/*
 * «Площадка» (AUT-542) loads lazily: each section is its own chunk, so the
 * first screen of the panel does not carry them.
 */
const SpecialistsListPage = lazy(() =>
  import('../pages/specialists').then((m) => ({ default: m.SpecialistsListPage })),
);
const CarListingsListPage = lazy(() =>
  import('../pages/car-listings').then((m) => ({ default: m.CarListingsListPage })),
);
const ChatsListPage = lazy(() =>
  import('../pages/chats').then((m) => ({ default: m.ChatsListPage })),
);
const ContentReportsListPage = lazy(() =>
  import('../pages/content-reports').then((m) => ({ default: m.ContentReportsListPage })),
);
const LeadsListPage = lazy(() =>
  import('../pages/leads').then((m) => ({ default: m.LeadsListPage })),
);
const VitrineCollectionsPage = lazy(() =>
  import('../pages/vitrine').then((m) => ({ default: m.CollectionsPage })),
);
const VitrineRbPricesPage = lazy(() =>
  import('../pages/vitrine').then((m) => ({ default: m.RbPricesPage })),
);
const CarRequestsListPage = lazy(() =>
  import('../pages/car-requests').then((m) => ({ default: m.CarRequestsListPage })),
);
const ReviewsListPage = lazy(() =>
  import('../pages/reviews').then((m) => ({ default: m.ReviewsListPage })),
);

const PageFallback = (): ReactElement => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
);

/**
 * A lazy «Площадка» list with its `:id` child: the page reads the id via
 * `useMatch`, the child only makes the path match (as `VehicleCatalogMake`),
 * so the list keeps its state while the drawer opens and closes. Without a
 * `detailPath` («Отзывы» — no single-review endpoint) the card is page state.
 *
 * With `perm` the route itself is gated: without the permission the operator
 * gets the shared no-access view and the page's chunk is never loaded.
 */
const lazyListRoute = (
  path: AppRoutePath,
  detailPath: AppRoutePath | null,
  page: ReactElement,
  perm?: AdminPermission,
): RouteObject => {
  const lazyPage = <Suspense fallback={<PageFallback />}>{page}</Suspense>;
  return {
    path,
    element: perm ? <RequirePermission perm={perm}>{lazyPage}</RequirePermission> : lazyPage,
    ...(detailPath ? { children: [{ path: detailPath, element: null }] } : {}),
  };
};

/**
 * Router topology:
 *
 *   /                     → redirect to Routes.App.Dashboard
 *   /auth/login           → LoginPage (no AuthGate — public)
 *   /auth/totp            → reserved deep-link to TOTP step
 *   /auth/accept          → SetPasswordPage (invite-acceptance, public)
 *   /auth/reset           → ResetRequestPage (public)
 *   /auth/reset/confirm   → ResetConfirmPage (public)
 *   /dashboard            → AppShell + DashboardPage (AuthGate-protected)
 *   /money-health         → AppShell + MoneyHealthPage (AuthGate-protected; page self-gates on process_payouts)
 *   /health               → AppShell + HealthPage (AuthGate-protected)
 *   /orders               → AppShell + OrdersListPage (AuthGate-protected)
 *   /users                → AppShell + UsersListPage (AuthGate-protected)
 *   /users/:id            → the same page with that user's drawer open (nested, AUT-542)
 *   /inspectors           → AppShell + InspectorsListPage (AuthGate-protected)
 *   /pickers              → AppShell + PickersListPage (AuthGate-protected, AUT-400)
 *   /companies            → AppShell + CompaniesListPage (AuthGate-protected; page self-gates on verify_inspectors, AUT-403)
 *   /companies/new        → AppShell + CompanyCreatePage (same gate)
 *   /companies/:id        → AppShell + CompanyDetailPage (same gate; `?tab=` picks the tab)
 *   /vehicle-catalog      → AppShell + VehicleCatalogPage (page self-gates on moderate_reports, AUT-437)
 *   /vehicle-catalog/:makeId → the same page with that make's drawer open (nested, so the
 *                           list keeps its state while drawers open and close)
 *   /specialists(/:id)    → «Специалисты», lazy (AUT-542; the route gates on verify_inspectors)
 *   /car-listings(/:id)   → «Объявления», lazy (AUT-542; route gate verify_inspectors)
 *   /reviews              → «Отзывы», lazy (AUT-542 PR2; route gate moderate_reports)
 *   /content-reports(/:id) → «Жалобы», lazy (AUT-542; route gate moderate_reports)
 *   /car-requests(/:id)   → «Заявки», lazy (AUT-542; route gate moderate_reports)
 *                           (`:id` — the list with that card's drawer open, nested)
 *   /reports              → AppShell + ReportsListPage (AuthGate-protected)
 *   /appeals              → AppShell + AppealsListPage (AuthGate-protected; page self-gates on resolve_appeals)
 *   /payouts              → AppShell + PayoutsListPage (AuthGate-protected)
 *   /refunds              → AppShell + RefundsListPage (AuthGate-protected; page self-gates on process_payouts)
 *   /cards                → AppShell + CardsListPage (AuthGate-protected; page self-gates on process_payouts)
 *   /config               → AppShell + ConfigPage (AuthGate-protected; page self-gates on manage_settings)
 *   *                     → NotFoundPage
 *
 * All paths sourced from `Routes` per CLAUDE.md absolute rule #12.
 *
 * Exported as `routerConfig` so tests can mount the same routes under
 * `createMemoryRouter` (jsdom's `Request` constructor is incompatible
 * with the data-router fetch primitives, so production uses
 * `createBrowserRouter` and unit tests use the in-memory variant).
 */
export const routerConfig: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to={Routes.App.Dashboard} replace />,
  },
  {
    path: Routes.Auth.Login,
    element: <LoginPage />,
  },
  {
    path: Routes.Auth.Accept,
    element: <SetPasswordPage />,
  },
  {
    path: Routes.Auth.ResetRequest,
    element: <ResetRequestPage />,
  },
  {
    path: Routes.Auth.ResetConfirm,
    element: <ResetConfirmPage />,
  },
  {
    element: <AuthGate />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: Routes.App.Dashboard, element: <DashboardPage /> },
          { path: Routes.App.MoneyHealth, element: <MoneyHealthPage /> },
          { path: Routes.App.Health, element: <HealthPage /> },
          { path: Routes.App.Orders, element: <OrdersListPage /> },
          {
            path: Routes.App.Users,
            element: <UsersListPage />,
            children: [{ path: Routes.App.UserDetail, element: null }],
          },
          { path: Routes.App.Inspectors, element: <InspectorsListPage /> },
          { path: Routes.App.Pickers, element: <PickersListPage /> },
          { path: Routes.App.Companies, element: <CompaniesListPage /> },
          { path: Routes.App.CompanyNew, element: <CompanyCreatePage /> },
          { path: Routes.App.CompanyDetail, element: <CompanyDetailPage /> },
          {
            path: Routes.App.VehicleCatalog,
            element: <VehicleCatalogPage />,
            // The page reads the id via `useMatch`; the child only makes the path match.
            children: [{ path: Routes.App.VehicleCatalogMake, element: null }],
          },
          lazyListRoute(
            Routes.App.Specialists,
            Routes.App.SpecialistDetail,
            <SpecialistsListPage />,
            'verify_inspectors',
          ),
          lazyListRoute(
            Routes.App.CarListings,
            Routes.App.CarListingDetail,
            <CarListingsListPage />,
            'verify_inspectors',
          ),
          lazyListRoute(Routes.App.Reviews, null, <ReviewsListPage />, 'moderate_reports'),
          lazyListRoute(
            Routes.App.ContentReports,
            Routes.App.ContentReportDetail,
            <ContentReportsListPage />,
            'moderate_reports',
          ),
          lazyListRoute(
            Routes.App.Chats,
            Routes.App.ChatDetail,
            <ChatsListPage />,
            'moderate_reports',
          ),
          lazyListRoute(
            Routes.App.CarRequests,
            Routes.App.CarRequestDetail,
            <CarRequestsListPage />,
            'moderate_reports',
          ),
          lazyListRoute(Routes.App.Leads, null, <LeadsListPage />, 'view_pii'),
          lazyListRoute(
            Routes.App.VitrineCollections,
            null,
            <VitrineCollectionsPage />,
            'manage_settings',
          ),
          lazyListRoute(
            Routes.App.VitrineRbPrices,
            null,
            <VitrineRbPricesPage />,
            'manage_settings',
          ),
          { path: Routes.App.Reports, element: <ReportsListPage /> },
          { path: Routes.App.Appeals, element: <AppealsListPage /> },
          { path: Routes.App.Payouts, element: <PayoutsListPage /> },
          { path: Routes.App.Refunds, element: <RefundsListPage /> },
          { path: Routes.App.Cards, element: <CardsListPage /> },
          { path: Routes.App.Config, element: <ConfigPage /> },
        ],
      },
    ],
  },
  {
    path: Routes.NotFound,
    element: <NotFoundPage />,
  },
];

const router = createBrowserRouter(routerConfig);

export const Router = (): ReactElement => {
  return <RouterProvider router={router} />;
};
